import React, { useState, useEffect, useCallback} from 'react';
import Layout from '../../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { useAuth } from '../../../context/AuthContext';
import { Input } from '../../../components/ui/input';
import { Badge } from '../../../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { 
  Plus, 
  Search, 
  Eye, 
  Users, 
  Building2,
  RefreshCw,
  Filter,
  UserCheck,
  UserX,
  Clock
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

const EmployeeList = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({ total: 0, active: 0, onLeave: 0, terminated: 0 });
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL;


  const loadEmployees = useCallback(async () => {
    try {
      setLoading(true);
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const [empRes, countRes] = await Promise.all([
        axios.get(`${backendUrl}/api/hcm/employees${params}`),
        axios.get(`${backendUrl}/api/hcm/employees/count`)
      ]);
      setEmployees(empRes.data);
      
      // Load stats
      const [activeCount, onLeaveCount, terminatedCount] = await Promise.all([
        axios.get(`${backendUrl}/api/hcm/employees/count?status=ACTIVE`).catch(() => ({ data: { count: 0 } })),
        axios.get(`${backendUrl}/api/hcm/employees/count?status=ON_LEAVE`).catch(() => ({ data: { count: 0 } })),
        axios.get(`${backendUrl}/api/hcm/employees/count?status=TERMINATED`).catch(() => ({ data: { count: 0 } }))
      ]);
      
      setStats({
        total: countRes.data.count,
        active: activeCount.data.count,
        onLeave: onLeaveCount.data.count,
        terminated: terminatedCount.data.count
      });
    } catch (error) {
      console.error('Error loading employees:', error);
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, backendUrl]);

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      loadEmployees();
      return;
    }
    try {
      setLoading(true);
      const res = await axios.get(`${backendUrl}/api/hcm/employees/search?q=${searchTerm}`);
      setEmployees(res.data);
    } catch (error) {
      console.error('Error searching employees:', error);
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'ACTIVE': { color: 'bg-green-100 text-green-700', icon: UserCheck },
      'INACTIVE': { color: 'bg-gray-100 text-gray-600', icon: UserX },
      'ON_LEAVE': { color: 'bg-yellow-100 text-yellow-700', icon: Clock },
      'TERMINATED': { color: 'bg-red-100 text-red-700', icon: UserX },
      'RETIRED': { color: 'bg-orange-100 text-orange-700', icon: Users }
    };
    const config = statusConfig[status] || statusConfig['ACTIVE'];
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {status?.replace('_', ' ')}
      </Badge>
    );
  };

  const filteredEmployees = employees.filter(emp => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      emp.employee_id?.toLowerCase().includes(term) ||
      emp.full_name?.toLowerCase().includes(term) ||
      emp.position?.toLowerCase().includes(term) ||
      emp.work_email?.toLowerCase().includes(term)
    );
  });

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="employee-list-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Employee Directory</h1>
            <p className="text-gray-500 mt-1">Personnel Administration - Employee Master Data</p>
          </div>
          {canWrite('employee') && (
          <Button 
            onClick={() => navigate('/hcm/employees/create')}
            className="flex items-center gap-2 bg-stone-800 hover:bg-stone-700"
          >
            <Plus className="w-4 h-4" />
            New Employee
          </Button>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-white border-l-4 border-l-stone-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Total Employees</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <Users className="w-8 h-8 text-stone-700" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-l-4 border-l-green-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Active</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
                </div>
                <UserCheck className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-l-4 border-l-yellow-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase">On Leave</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.onLeave}</p>
                </div>
                <Clock className="w-8 h-8 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white border-l-4 border-l-red-500">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500 uppercase">Terminated</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.terminated}</p>
                </div>
                <UserX className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-gray-500" />
                <span className="text-sm font-medium text-gray-700">Filters:</span>
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="ACTIVE">Active</SelectItem>
                  <SelectItem value="ON_LEAVE">On Leave</SelectItem>
                  <SelectItem value="INACTIVE">Inactive</SelectItem>
                  <SelectItem value="TERMINATED">Terminated</SelectItem>
                  <SelectItem value="RETIRED">Retired</SelectItem>
                </SelectContent>
              </Select>
              
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by ID, name, position, or email..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10"
                />
              </div>
              
              <Button variant="outline" onClick={handleSearch}>
                Search
              </Button>
              
              <Button variant="ghost" onClick={loadEmployees}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Employee Table */}
        <Card>
          {loading ? (
            <CardContent className="p-8 text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
              <p className="mt-2 text-gray-500">Loading employees...</p>
            </CardContent>
          ) : filteredEmployees.length === 0 ? (
            <CardContent className="p-8 text-center">
              <Users className="w-12 h-12 mx-auto text-gray-300" />
              <h3 className="mt-4 text-lg font-medium text-gray-900">No employees found</h3>
              <p className="mt-2 text-gray-500">
                {employees.length === 0 
                  ? 'Start by creating your first employee record.'
                  : 'Try adjusting your search or filters.'}
              </p>
              {canWrite('employee') && employees.length === 0 && (
                <Button 
                  className="mt-4 bg-stone-800 hover:bg-stone-700"
                  onClick={() => navigate('/hcm/employees/create')}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Employee
                </Button>
              )}
            </CardContent>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Employee ID</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Position</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Hire Date</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEmployees.map((emp) => (
                  <TableRow key={emp.employee_id} className="hover:bg-gray-50">
                    <TableCell className="font-mono font-medium">{emp.employee_id}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium text-gray-900">{emp.full_name}</p>
                        {emp.work_email && (
                          <p className="text-xs text-gray-500">{emp.work_email}</p>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{emp.position || '-'}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Building2 className="w-3 h-3 text-gray-400" />
                        {emp.department || '-'}
                      </div>
                    </TableCell>
                    <TableCell>{emp.company_code}</TableCell>
                    <TableCell>{emp.hire_date ? new Date(emp.hire_date).toLocaleDateString() : '-'}</TableCell>
                    <TableCell>{getStatusBadge(emp.status)}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => navigate(`/hcm/employees/${emp.employee_id}`)}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Card>
      </div>
    </Layout>
  );
};

export default EmployeeList;
